import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './family-member-typeahead.css';

export interface FamilyMemberOption {
  familyMemberId: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  age?: string | null;
  spouseName?: string | null;
  birthYear?: string | null;
}

interface FamilyMemberTypeaheadProps {
  /** The currently selected family member ID */
  value: string;
  /** Callback when a family member is selected */
  onChange: (familyMemberId: string) => void;
  /** Optional callback when the input text changes */
  onInputChange?: (text: string) => void;
  /** List of family members to choose from */
  members: FamilyMemberOption[];
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Optional custom filter function */
  filterFunction?: (member: FamilyMemberOption, query: string) => boolean;
  /** Maximum number of results to show (default: 5) */
  maxResults?: number;
  /** Optional label for the input */
  label?: string;
  /** Optional help text */
  helpText?: string;
  /** Optional ID for the input element */
  id?: string;
}

/**
 * Reusable typeahead component for selecting family members
 */
export const FamilyMemberTypeahead: React.FC<FamilyMemberTypeaheadProps> = ({
  value,
  onChange,
  onInputChange,
  members,
  disabled = false,
  placeholder,
  filterFunction,
  maxResults = 5,
  label,
  helpText,
  id
}) => {
  const { t } = useTranslation('forms');
  const resolvedPlaceholder = placeholder ?? t('familyMemberTypeahead.placeholder');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the selected member
  const selectedMember = members.find((m) => m.familyMemberId === value);

  // Default filter function
  const defaultFilter = (member: FamilyMemberOption, query: string): boolean => {
    const queryLower = query.trim().toLowerCase();
    if (!queryLower) return true;
    return member.fullName.toLowerCase().includes(queryLower);
  };

  const filterFn = filterFunction || defaultFilter;

  // Filter members based on search query
  const getFilteredMembers = (): FamilyMemberOption[] => {
    if (!searchQuery.trim()) {
      return members.slice(0, maxResults);
    }
    return members.filter((member) => filterFn(member, searchQuery)).slice(0, maxResults);
  };

  const filteredMembers = getFilteredMembers();
  const totalFilteredCount = searchQuery.trim()
    ? members.filter((member) => filterFn(member, searchQuery)).length
    : members.length;

  // Update search query when value changes externally (e.g., form reset)
  useEffect(() => {
    if (value && selectedMember) {
      setSearchQuery(selectedMember.fullName);
    } else if (!value) {
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedMember?.familyMemberId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        inputRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown && filteredMembers.length > 0 && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setShowDropdown(true);
      return;
    }

    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredMembers.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredMembers.length) {
          handleSelectMember(filteredMembers[highlightedIndex].familyMemberId);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelectMember = (memberId: string) => {
    onChange(memberId);
    const selected = members.find((m) => m.familyMemberId === memberId);
    setSearchQuery(selected?.fullName || '');
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onChange('');
    setSearchQuery('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setShowDropdown(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing something different
    if (value && newValue !== selectedMember?.fullName) {
      onChange('');
    }

    // Call optional input change callback
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  const handleFocus = () => {
    if (filteredMembers.length > 0) {
      setShowDropdown(true);
    }
  };

  /**
   * Generate disambiguation text for a family member option
   * Shows age/birth year and spouse name if available
   */
  const getDisambiguationText = (member: FamilyMemberOption): string | null => {
    const parts: string[] = [];

    // Add age or birth year
    if (member.age) {
      parts.push(member.age);
    } else if (member.birthYear) {
      parts.push(t('familyMemberTypeahead.bornYear', { year: member.birthYear }));
    }

    // Add spouse name
    if (member.spouseName) {
      parts.push(t('familyMemberTypeahead.spousePrefix', { name: member.spouseName }));
    }

    return parts.length > 0 ? parts.join(' • ') : null;
  };

  return (
    <div className="family-member-typeahead">
      {label && (
        <label htmlFor={id || 'family-member-typeahead'} className="family-member-typeahead-label">
          {label}
        </label>
      )}
      <div className="family-member-typeahead-input-wrapper">
        <input
          ref={inputRef}
          id={id || 'family-member-typeahead'}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="family-member-typeahead-input"
          placeholder={resolvedPlaceholder}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="family-member-typeahead-clear"
            disabled={disabled}
            aria-label={t('actions.clearSelection')}
          >
            ×
          </button>
        )}
      </div>
      {showDropdown && filteredMembers.length > 0 && (
        <div ref={dropdownRef} className="family-member-typeahead-dropdown">
          {filteredMembers.map((member, index) => {
            const disambiguationText = getDisambiguationText(member);
            return (
              <button
                key={member.familyMemberId}
                type="button"
                onClick={() => handleSelectMember(member.familyMemberId)}
                className={`family-member-typeahead-option ${
                  index === highlightedIndex ? 'family-member-typeahead-option--highlighted' : ''
                }`}
                disabled={disabled}
              >
                <div className="family-member-typeahead-option-content">
                  <div className="family-member-typeahead-option-name">{member.fullName}</div>
                  {disambiguationText && (
                    <div className="family-member-typeahead-option-details">{disambiguationText}</div>
                  )}
                </div>
              </button>
            );
          })}
          {members.length > maxResults && (
            <div className="family-member-typeahead-footer">
              {filteredMembers.length === maxResults && searchQuery.trim()
                ? t('familyMemberTypeahead.showingTop', { shown: maxResults, total: totalFilteredCount })
                : members.length > maxResults
                  ? t('familyMemberTypeahead.totalMembersTypeToFilter', { count: members.length })
                  : null}
            </div>
          )}
        </div>
      )}
      {showDropdown && searchQuery.trim() && filteredMembers.length === 0 && (
        <div ref={dropdownRef} className="family-member-typeahead-dropdown">
          <div className="family-member-typeahead-empty">{t('familyMemberTypeahead.noMatching')}</div>
        </div>
      )}
      {helpText && <p className="family-member-typeahead-help-text">{helpText}</p>}
    </div>
  );
};

export default FamilyMemberTypeahead;
